import { useEffect, useState } from "react";
import axios from "../api/axios";
import { extractRoles, hasAnyRole, normalizeRoleInput } from "../utils/roleHelpers";
import { extractPermissions, hasPermission } from "../utils/permissionHelpers";
import { AdminApiError, ADMIN_SESSION_EXPIRED_EVENT } from "../api/errors";

interface AdminLoginProps {
  onLoginSuccess: () => void | Promise<void>;
  allowedRoles?: string[];
  requiredPermission?: string;
  title?: string;
  description?: string;
}

export default function AdminLogin({
  onLoginSuccess,
  allowedRoles = ["admin"],
  requiredPermission,
  title = "Admin Girisi",
  description,
}: AdminLoginProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setError("");
    setLoading(true);

    try {
      await axios.post("/auth/login", { email, password });
      const res = await axios.get("/auth/me");

      const roles = extractRoles(res.data);
      const permissions = extractPermissions(res.data);
      const normalizedAllowed = allowedRoles
        .map((role) => normalizeRoleInput(role))
        .filter((role): role is NonNullable<ReturnType<typeof normalizeRoleInput>> => Boolean(role));

      if (normalizedAllowed.length && !hasAnyRole(roles, normalizedAllowed)) {
        setError("Bu alana erisim iznin yok.");
        setLoading(false);
        return;
      }

      if (requiredPermission && !hasPermission(permissions, requiredPermission)) {
        setError("Bu ekrana giris yetkin yok.");
        setLoading(false);
        return;
      }

      await Promise.resolve(onLoginSuccess());
    } catch (err) {
      if (err instanceof AdminApiError) {
        setError(err.message || "Giris basarisiz. E-posta veya sifreyi kontrol et.");
      } else {
        console.error("Admin login failed", err);
        setError("Giris basarisiz. E-posta veya sifreyi kontrol et.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = () => {
      setError("Oturum sonlandi. Lutfen tekrar giris yap.");
    };
    window.addEventListener(ADMIN_SESSION_EXPIRED_EVENT, handler);
    return () => {
      window.removeEventListener(ADMIN_SESSION_EXPIRED_EVENT, handler);
    };
  }, []);

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-4 py-8">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_18%,rgba(10,132,255,0.34),transparent_36%),radial-gradient(circle_at_88%_4%,rgba(0,168,126,0.25),transparent_26%),linear-gradient(145deg,#061024_0%,#11213d_42%,#1d3358_100%)]" />
      <div className="absolute -right-20 -top-24 h-72 w-72 rounded-full bg-sky-500/20 blur-3xl" />
      <div className="absolute -bottom-16 -left-16 h-64 w-64 rounded-full bg-emerald-400/20 blur-3xl" />

      <div className="relative w-full max-w-md rounded-[28px] border border-white/20 bg-white/80 p-7 shadow-[0_30px_60px_rgba(6,18,36,0.35)] backdrop-blur-xl">
        <div className="mb-6">
          <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-sky-600">Zenitoon Workspace</div>
          <h2 className="mt-2 text-2xl font-extrabold tracking-tight text-slate-900">{title}</h2>
          <p className="mt-2 text-sm text-slate-600">
            {description ?? "Yonetim paneline erismek icin yetkili hesap bilgilerinle devam et."}
          </p>
        </div>

        <div className="space-y-3">
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
            E-posta
            <input
              type="email"
              placeholder="admin@zenitoon.com"
              className="mt-1 w-full rounded-xl border border-slate-300/90 bg-white/90 px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-200/40"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="username"
            />
          </label>

          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
            Sifre
            <input
              type="password"
              placeholder="********"
              className="mt-1 w-full rounded-xl border border-slate-300/90 bg-white/90 px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-200/40"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
            />
          </label>

          <button
            type="button"
            onClick={handleLogin}
            disabled={loading}
            className="mt-2 w-full rounded-xl bg-[linear-gradient(135deg,#0a84ff,#0a5dd7)] px-4 py-2.5 text-sm font-bold text-white shadow-[0_14px_26px_rgba(10,109,230,0.36)] transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-65"
          >
            {loading ? "Giris yapiliyor..." : "Giris Yap"}
          </button>
        </div>

        {error && (
          <div className="mt-4 rounded-xl border border-rose-300/55 bg-rose-50/80 px-3 py-2 text-sm text-rose-700">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
